import { memo, useState, useMemo, useContext } from 'react';
import { FixedSizeGrid as VirtualGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Box from '@mui/material/Box';
import { ShopDataContext } from './_ShopViewContext';
import FilterBar from './FilterBar';
import FilterDrawer from './FilterDrawer';
import ItemCard from './ItemCard';
import ItemDrawer from './ItemDrawer';

function ItemsGrid() {
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [itemDrawerOpen, setItemDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const { data } = useContext(ShopDataContext);

  const [filter, setFilter] = useState({
    search: '',
    description: '',
    manufacturers: [],
    manufacturerSearch: '',
    subcategories: [],
    subcategorySearch: '',
    minPrice: '',
    maxPrice: '',
    inCart: '',
    sku: '',
    productId: '',
  });

  // Get unique manufacturers for dropdown
  const manufacturers = useMemo(
    () =>
      Array.from(new Set(data.map((row) => row.productDetails[0]?.manufacturer).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [data]
  );

  // Filter logic
  const filteredData = useMemo(() => {
    const searchAll = (filter?.searchAll ?? '')
      .toLowerCase()
      .split(' ')
      .filter((val) => val);
    const result = data.filter((row) => {
      const details = row?.productDetails?.[0] || {};
      const description = row?.description ?? '';
      const manufacturer = details?.manufacturer ?? '';
      const sku = details?.vendorItemId ?? '';
      const psId = details?.productId ? `PS${details.productId}` : '';
      const manufacturerId = details?.manufacturerId ?? '';
      const subcategoryName = row?.displaySubcategory?.name ?? '';
      const uomAmount = details?.uomAmount ?? '';
      const uomType = details?.uomType ?? '';

      const searchTerm = String(filter?.search ?? '').toLowerCase();
      const descriptionFilter = String(filter?.description ?? '').toLowerCase();
      let matchesSearchAll = !searchAll.length;
      if (!matchesSearchAll) {
        const searchArray = [
          description,
          manufacturer,
          sku,
          psId,
          manufacturerId,
          subcategoryName,
          `${uomAmount}/${uomType}`,
          details?.vendor ?? '',
        ].map((val) => String(val).toLowerCase());
        matchesSearchAll = searchAll.every((val) => searchArray.some((srcVal) => srcVal.includes(val)));
      }

      const matchesSearch =
        !searchTerm ||
        description.toLowerCase().includes(searchTerm) ||
        manufacturer.toLowerCase().includes(searchTerm);

      const matchesDescription = !descriptionFilter || description.toLowerCase().includes(descriptionFilter);

      const matchesManufacturer =
        !Array.isArray(filter?.manufacturers) || filter.manufacturers.length === 0
          ? true
          : filter.manufacturers.some((m) => String(m).toLowerCase() === manufacturer.toLowerCase());

      const matchesSubcategory =
        !Array.isArray(filter?.subcategories) || filter.subcategories.length === 0
          ? true
          : filter.subcategories.some((s) => String(s).toLowerCase() === subcategoryName.toLowerCase());

      const matchesMinPrice = filter?.minPrice === '' || details?.price >= Number(filter.minPrice);
      const matchesMaxPrice = filter?.maxPrice === '' || details?.price <= Number(filter.maxPrice);

      const matchesInCart =
        !('inCart' in (filter || {})) || filter.inCart === ''
          ? true
          : filter.inCart === 'true'
          ? row.quantity > 0
          : row.quantity === 0;

      const matchesSku = !filter.sku || sku.toLowerCase().includes(String(filter.sku).toLowerCase());
      const matchesPsId =
        !filter.productId || String(psId).toLowerCase().includes(String(filter.productId).toLowerCase());
      const matchesManufacturerId =
        !filter.manufacturerId ||
        String(manufacturerId).toLowerCase().includes(String(filter.manufacturerId).toLowerCase());

      const matchesVendor = !filter.vendor?.length || filter.vendor.includes(row.productDetails[0]?.vendor);

      const matchesUom = (() => {
        if (!filter.uom) return true;
        const normalizedFilter = String(filter.uom).replace(/[\\/]/g, ' ').toLowerCase().trim();
        const normalizedUomCombined = `${uomAmount} ${uomType}`.toLowerCase().trim();
        return (
          normalizedUomCombined.includes(normalizedFilter) ||
          String(uomAmount).toLowerCase().includes(normalizedFilter) ||
          String(uomType).toLowerCase().includes(normalizedFilter)
        );
      })();

      return (
        matchesSearch &&
        matchesDescription &&
        matchesManufacturer &&
        matchesSubcategory &&
        matchesMinPrice &&
        matchesMaxPrice &&
        matchesInCart &&
        matchesSku &&
        matchesPsId &&
        matchesManufacturerId &&
        matchesVendor &&
        matchesSearchAll &&
        matchesUom
      );
    });

    // SORT LOGIC
    return result.sort((a, b) => {
      const aQty = a.quantity ?? 0;
      const bQty = b.quantity ?? 0;
      if (aQty !== bQty) return bQty - aQty;

      const aPrev = a.productDetails?.[0]?.previouslyOrderedItems?.length ?? 0;
      const bPrev = b.productDetails?.[0]?.previouslyOrderedItems?.length ?? 0;
      if (aPrev !== bPrev) return bPrev - aPrev;
      return 0;
    });
  }, [data, filter]);

  const subcategories = Array.from(new Set(data.map((row) => row.displaySubcategory?.name).filter(Boolean)));
  const allVendors = Array.from(new Set(data.map((row) => row.productDetails?.[0]?.vendor).filter(Boolean))).sort(
    (a, b) => a.localeCompare(b)
  );

  return (
    <>
      <FilterBar
        filter={filter}
        setFilter={setFilter}
        setFilterDrawerOpen={setFilterDrawerOpen}
        manufacturers={manufacturers}
        subcategories={subcategories}
        allVendors={allVendors}
      />

      <FilterDrawer
        open={filterDrawerOpen}
        setOpen={setFilterDrawerOpen}
        filter={filter}
        setFilter={setFilter}
        manufacturers={manufacturers}
        subcategories={subcategories}
        allVendors={allVendors}
      />

      {/* Virtualized Product Grid */}
      <Box className="item-scrollbar" sx={{ height: 'calc(100vh - 225px)', width: '100%', p: 2 }}>
        <AutoSizer>
          {({ height, width }) => {
            const columnCount = 5;
            const gap = 4;
            const itemHeight = 380;
            const totalGapWidth = columnCount * gap;
            const columnWidth = (width - totalGapWidth) / columnCount;
            const rowCount = Math.ceil(filteredData.length / columnCount);

            return (
              <VirtualGrid
                className="item-scrollbar"
                columnCount={columnCount}
                columnWidth={columnWidth}
                height={height}
                rowCount={rowCount}
                rowHeight={itemHeight}
                width={width}
              >
                {({ columnIndex, rowIndex, style }) => {
                  const index = rowIndex * columnCount + columnIndex;
                  const row = filteredData[index];
                  if (!row) return null;

                  return (
                    <Box key={row.id} style={style} sx={{ p: 1 }}>
                      <ItemCard
                        row={row}
                        editingItemId={editingItemId}
                        setEditingItemId={setEditingItemId}
                        onOpenDetails={() => {
                          setSelectedItem(row);
                          setItemDrawerOpen(true);
                        }}
                      />
                    </Box>
                  );
                }}
              </VirtualGrid>
            );
          }}
        </AutoSizer>
      </Box>

      <ItemDrawer
        open={itemDrawerOpen}
        onClose={() => { setItemDrawerOpen(false); setEditingItemId(null); }}
        selectedItem={selectedItem}
        editingItemId={editingItemId}
        setEditingItemId={setEditingItemId}
      />
    </>
  );
}

export default memo(ItemsGrid);
